import { Flex, Grid, Image } from '@chakra-ui/react';
import { useSelectIsL2Visible } from 'Deer/slice/DeerNav.slice';
import { useDispatch } from 'react-redux';
import { setReviewCreationKey, useReviewCreationV1Data } from 'reducers/reviewCreationV1Slice';
import { checkEnabledSimplifiedReviewDemoPresets } from 'utils/helpers/featureFlags';

import { Recurring } from 'assets/assetsV1/iconComponents/Recurring';
import MeshLogoImage from 'assets/assetsV1/icons/specialIcons/meshLogo.svg';
import EventTypeImage from 'assets/assetsV1/illustrations/deerIllustrations/event_type.svg';
import AnnualReviewImage from 'assets/assetsV1/illustrations/deerIllustrations/reviewPresetTypes/annualReview.png';
// import CompetencyOnlyReviewImage from 'assets/assetsV1/illustrations/deerIllustrations/reviewPresetTypes/competencyOnlyReview.png';
import CustomReviewImage from 'assets/assetsV1/illustrations/deerIllustrations/reviewPresetTypes/customReview.png';
import PipReviewImage from 'assets/assetsV1/illustrations/deerIllustrations/reviewPresetTypes/pipReview.png';
import ProbationReviewImage from 'assets/assetsV1/illustrations/deerIllustrations/reviewPresetTypes/probationReview.png';
import QuarterlyReviewImage from 'assets/assetsV1/illustrations/deerIllustrations/reviewPresetTypes/quarterlyReview.png';

import { isNetchexLogin } from 'constants/netChexConstants';

import { PaperWithShadow } from 'uiToolkitV1/Paper';
import { RadioCard } from 'uiToolkitV1/RadioCard/RadioCard';
import { TextLabel, TextLabelSmall, TextBodyMedium } from 'uiToolkitV1/Typography';

import { ReviewProcessV2PresetsFooter } from './ReviewProcessV2PresetsFooter';
import { REVIEW_PRESET_TYPES } from './constants/reviewCreationConstants';

export const ImgIconTag = ({ label, imgSrc = null, Icon = null, withImagePadding = false }) => (
  <Flex py="2px" px={1} bg="neutral.F2F2F2" gap="4px" borderRadius="4px" alignItems="center">
    {imgSrc && <Image src={imgSrc} width={withImagePadding ? '14px' : '16px'} />}
    {Icon && <Icon color="icon.g2" />}
    <TextLabelSmall>{label}</TextLabelSmall>
  </Flex>
);

const REVIEW_PRESETS = [
  {
    type: REVIEW_PRESET_TYPES.ANNUAL_APPRAISAL,
    title: 'Annual review',
    subText:
      'A comprehensive year-end evaluation assessing overall performance, competencies, achievements, challenges, and development areas to support growth.',
    imgSrc: AnnualReviewImage,
  },
  {
    type: REVIEW_PRESET_TYPES.QUARTERLY_REVIEW,
    title: 'Quarterly review',
    subText: 'A lightweight, frequent review to track progress and ensure continuous feedback and alignment on goals.',
    imgSrc: QuarterlyReviewImage,
  },

  {
    type: REVIEW_PRESET_TYPES.PROBATION_REVIEW,
    title: 'Probation review (Single instance)',
    subText: 'An assessment for new hires at the end of their probation period to determine role fit and next steps.',
    imgSrc: ProbationReviewImage,
  },
  {
    type: REVIEW_PRESET_TYPES.PIP_REVIEW,
    title: 'PIP review',
    subText:
      'A structured review for employees on a Performance Improvement Plan to track progress against the decided action plan.',
    imgSrc: PipReviewImage,
    isDemoPreset: true,
  },
  // {
  //   type: 'COMPETENCY_ASSESSMENT',
  //   title: 'Competency-Only Review',
  //   subText: 'A focused review evaluating employees solely on key skills and competencies.',
  //   imgSrc: CompetencyOnlyReviewImage,
  // },
  {
    type: REVIEW_PRESET_TYPES.WORK_ANNIVERSARY_REVIEW,
    title: 'Work anniversary review',
    subText:
      "An evaluation on an employee's work-anniversary to assess performance, growth, and contributions, guiding development and future goals.",
    imgSrc: AnnualReviewImage,
    isDemoPreset: true,
  },
  {
    type: REVIEW_PRESET_TYPES.PROBATION_30_60_90,
    title: 'Probation check-in (30-60-90 days)',
    subText:
      'A series of structured evaluations in the first months of employment to assess performance, adjustment, and role alignment, ensuring timely feedback and support.',
    imgSrc: ProbationReviewImage,
    isDemoPreset: true,
  },
  {
    type: REVIEW_PRESET_TYPES.CUSTOM,
    title: 'Custom cycle',
    subText: 'A flexible review setup allowing you to customize all elements of the performance review as needed.',
    imgSrc: CustomReviewImage,
  },
];

export const ReviewProcessV2Presets = () => {
  const { selectedReviewCreationPreset } = useReviewCreationV1Data();

  const dispatch = useDispatch();
  const isL2NavVisible = useSelectIsL2Visible();

  const handlePresetChange = (selectedPreset) => {
    dispatch(setReviewCreationKey({ selectedReviewCreationPreset: selectedPreset }));
  };

  const reviewPresetsToShow = checkEnabledSimplifiedReviewDemoPresets()
    ? REVIEW_PRESETS
    : REVIEW_PRESETS.filter((preset) => !preset.isDemoPreset);

  const getAdditionalTags = (type) => {
    switch (type) {
      case REVIEW_PRESET_TYPES.ANNUAL_APPRAISAL:
        // case REVIEW_PRESET_TYPES.COMPETENCY_ASSESSMENT:
        return (
          <Flex gap={1}>
            <ImgIconTag
              label={isNetchexLogin ? 'Recommended' : 'Mesh recommended'}
              imgSrc={isNetchexLogin ? null : MeshLogoImage}
              withImagePadding
            />
          </Flex>
        );
      case REVIEW_PRESET_TYPES.QUARTERLY_REVIEW:
        return (
          <Flex gap={1}>
            {/* <ImgIconTag label="Recurring - Once every 3 months" imgSrc={RecurringTagImage} /> */}
            <ImgIconTag
              label={isNetchexLogin ? 'Recommended' : 'Mesh recommended'}
              imgSrc={isNetchexLogin ? null : MeshLogoImage}
              withImagePadding
            />
          </Flex>
        );
      case REVIEW_PRESET_TYPES.PIP_REVIEW:
        return (
          <Flex gap={1}>
            <ImgIconTag label="Event - Based on review outcome" imgSrc={EventTypeImage} />
            <ImgIconTag
              label={isNetchexLogin ? 'Recommended' : 'Mesh recommended'}
              imgSrc={isNetchexLogin ? null : MeshLogoImage}
              withImagePadding
            />
          </Flex>
        );
      case REVIEW_PRESET_TYPES.PROBATION_REVIEW:
        return (
          <Flex gap={1}>
            <ImgIconTag label="Event - Post date of joining" imgSrc={EventTypeImage} />
            <ImgIconTag
              label={isNetchexLogin ? 'Recommended' : 'Mesh recommended'}
              imgSrc={isNetchexLogin ? null : MeshLogoImage}
              withImagePadding
            />
          </Flex>
        );
      case REVIEW_PRESET_TYPES.WORK_ANNIVERSARY_REVIEW:
        return (
          <Flex gap={1}>
            <ImgIconTag label="Event - Employee work anniversary" imgSrc={EventTypeImage} />
            <ImgIconTag label="Recurring - Every year" Icon={Recurring} />
          </Flex>
        );

      case REVIEW_PRESET_TYPES.PROBATION_30_60_90:
        return (
          <Flex gap={1}>
            <ImgIconTag label="Event - Post date of joining" imgSrc={EventTypeImage} />
            <ImgIconTag label="Set of 3 reviews" Icon={Recurring} />
          </Flex>
        );

      case REVIEW_PRESET_TYPES.CUSTOM:
      default:
        return null;
    }
  };

  return (
    <PaperWithShadow
      display="flex"
      flexDir="column"
      py={0}
      flex={1}
      overflow="auto"
      ml={isL2NavVisible || isNetchexLogin ? '0' : '34px'}
      transition="margin .3s ease;"
    >
      <Flex flex={1} p={4} overflow="auto" borderRadius="8px" bg="white" flexDir="column">
        <TextBodyMedium bold>What type of review do you want to launch?</TextBodyMedium>

        <TextLabel color="text.g2" mb={3}>
          Pick the review type you want to launch and modify the pre-filled form according to your needs in the next
          step.
        </TextLabel>

        <Flex flex={1} p={2} borderRadius="8px" bg="surface.F5F7F9" flexDir="column" gap={2}>
          <Grid gridTemplateColumns="1fr 1fr" gridGap={2}>
            {reviewPresetsToShow.map((preset) => (
              <RadioCard
                key={preset.type}
                title={preset.title}
                subText={preset.subText}
                imgSrc={preset.imgSrc}
                onClick={() => handlePresetChange(preset.type)}
                checked={selectedReviewCreationPreset === preset.type}
                additionalContent={getAdditionalTags(preset.type)}
                showBetaTag={preset.isDemoPreset}
              />
            ))}
          </Grid>
        </Flex>
      </Flex>
      <ReviewProcessV2PresetsFooter />
    </PaperWithShadow>
  );
};
